/**
 * User-facing error message sanitization.
 *
 * Maps known backend error `detail` strings to safe, localised messages
 * and provides context-specific fallbacks so that raw server internals
 * are never shown to end-users.
 *
 * This is a plain module (no React hook available), so translations are read
 * from the shared i18n instance via `i18n.t()`. Messages resolve against the
 * active language at call time.
 */

import i18n from "../i18n";

// Backend `detail` string → i18n key under "errors.known".
const KNOWN_ERROR_KEYS = {
  "Invalid credentials": "invalidCredentials",
  "Incorrect user or password": "invalidCredentials",
  "User already registered": "userAlreadyRegistered",
  "User not authorized for registration": "userNotAuthorized",
  "Not authorized": "notAuthorized",
  "Not enough permissions": "notEnoughPermissions",
  "Organization not found": "organizationNotFound",
  "Phone number already registered": "phoneAlreadyRegistered",
  "User already in allowed users list": "userAlreadyAllowed",
  "Could not validate credentials": "sessionExpired",
};

const ACTION_CONTEXTS = new Set([
  "login",
  "register",
  "save",
  "delete",
  "load",
  "create",
  "generic",
]);

/**
 * Extract a user-safe error message from an Axios error.
 *
 * @param {Error} err - The caught error (typically an Axios error).
 * @param {string} [context="generic"] - One of the action contexts.
 * @returns {string} A safe, user-facing error message.
 */
export function getUserErrorMessage(err, context = "generic") {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && KNOWN_ERROR_KEYS[detail]) {
    return i18n.t(`errors.known.${KNOWN_ERROR_KEYS[detail]}`);
  }
  const action = ACTION_CONTEXTS.has(context) ? context : "generic";
  return i18n.t(`errors.actions.${action}`);
}
